import React, {useState} from 'react';
import useConstant from 'use-constant';
import {useAsync} from 'react-async';
import awesomeDebouncePromise from 'awesome-debounce-promise';
import useAutosuggest from '@stencil/use-autosuggest';

const Autosuggest = props => {
  const ListElement = props => <div>{props.children}</div>;
  const InputElement = props => <input {...props} />;
  const itemElementDefault = item => <div>{item.n}</div>;
  const onItemSelectionDefault = change => {};
  const itemToStringDefault = item => item.n;
  const massageDataDefault = items => items;

  const {
    initialInputValue = '',
    placeholder = '',
    onItemSelection = onItemSelectionDefault,
    itemToString = itemToStringDefault,
    itemElement = itemElementDefault,
    getUrl,
    Input = InputElement,
    List = ListElement,
    massageData = massageDataDefault,
  } = props;

  const [displayOverlay, setDisplayOverlay] = useState(false);

  const DEBOUNCE_TIMEOUT = 400;

  const toggleOverlay = flag => {
    setDisplayOverlay(flag);
  };

  const locationSearchURL = (search_query: string) => {
    return getUrl(search_query);
  };

  const fetchLocationSuggestions = async ({inputValue}, {signal}) => {
    const fetchVoyagerUrlForInput = locationSearchURL(inputValue);
    if (!inputValue) {
      return [];
    }

    const res = await fetch(fetchVoyagerUrlForInput, {signal});
    if (!res.ok) throw new Error(res.statusText);
    const ret = await res.json();

    if (ret.data && ret.data.documents) {
      return ret.data.documents;
    }
  };

  const debouncedFetchPromise = useConstant(() =>
    awesomeDebouncePromise(fetchLocationSuggestions, DEBOUNCE_TIMEOUT),
  );

  const inputRef = React.useRef(null);


  const {
    propGetters: {getInputProps, getMenuProps, getItemProps, getRootProps, getLabelProps},
    state: {isOpen, highlightedIndex, selectedItem, inputValue},
  } = useAutosuggest({
    itemToString,
    initialInputValue,
    onSelectedItemChange(changes) {
      onItemSelection(changes.selectedItem);
      toggleOverlay(false);
    },
  });
  
  React.useEffect(() => {
    if (inputRef.current && isOpen) {
      const distanceBtwInputAndWindowBottom =
        window.innerHeight -
        (inputRef.current.getBoundingClientRect().y + inputRef.current.offsetHeight);

      const proportionalDistance = distanceBtwInputAndWindowBottom / window.innerHeight;
      const percentageDistance = proportionalDistance * 100;
      const factorOfReductionForMenuHeight = percentageDistance * 0.625;

      const menuHeight = factorOfReductionForMenuHeight;

      setMenuHeight(menuHeight);
    }
  }, [isOpen]);

  const {data: items = [], isPending, isRejected, error} = useAsync({
    promiseFn: debouncedFetchPromise,
    watch: inputValue,
    inputValue,
  });

  const itemsArr = massageData(items);

  return (
    <div>
      <Input
        autoFocus
        name="autosuggest"
        type="text"
        {...getInputProps({
          ref: inputRef,
          placeholder,
          onFocus: () => {
            toggleOverlay(true);
          },
          onBlur: () => {
            toggleOverlay(false);
          },
        })}
      />
      <List>
        <div {...getMenuProps()}>
          {(isOpen &&
            itemsArr.map((item, index) => {
              return (
                <div
                  key={item.id}
                  {...getItemProps({
                    item,
                    index,
                  })}
                >
                  {itemElement(item)}
                </div>
              );
            })) ||
            null}
        </div>
      </List>
    </div>
  );
};

export default Autosuggest;
